import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { HistoryOrder } from '~Scenes/History';
import { ReceiptType, useDispatch, useI18n } from '~Hooks';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { addToast } from '~/actions';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import MessageInput from '~ComponentsImproved/MessageInput';
import { useReportsApi } from '~Components/ReportsUniverse';
import { ORDER_STATUS } from '~../__queries__';
import { PrintReceiptArgs } from '~Scenes/History/view';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div<{ disabled: boolean }>`
  padding: 12px 16px;
  color: #333;
  background: #fff;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
  border-radius: 1px;
  margin-right: 20px;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const PrintIcon = styled.i`
  font-size: 21px;
  margin-right: 4px;
`;

const EmailMessage = styled.div<{ error: boolean }>`
  color: ${({ error }) => (error ? 'red' : 'black')};
`;

const SEND_ORDER_EMAIL = gql`
  mutation sendOrderEmail($orderId: ID!, $email: String) {
    sendOrderEmail(orderId: $orderId, email: $email) {
      success
      error
      message
    }
  }
`;

const CHANGE_HAS_PRINTED = gql`
  mutation updateOrder($id: ID!, $hasPrintedReceiptCopy: Boolean) {
    updateOrder(id: $id, hasPrintedReceiptCopy: $hasPrintedReceiptCopy) {
      id
      hasPrintedReceiptCopy
    }
  }
`;

interface Props {
  order: HistoryOrder;
  printReceipt: (args: PrintReceiptArgs) => void;
}

const PrintButton = ({ order, printReceipt }: Props) => {
  const reportsApi = useReportsApi();
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const [changeHasPrinted] = useMutation(CHANGE_HAS_PRINTED);
  const [sendOrderEmail, { loading }] = useMutation(SEND_ORDER_EMAIL);
  const { isShowing: emailModalShowing, toggle: toggleEmailModal } = useModal();
  const [emailSuccess, setEmailSuccess] = useState<boolean | undefined>();

  const handlePrint = (type: ReceiptType) => {
    if (!order) {
      return;
    }

    printReceipt({ type, cardDetails: order.payment.metadata?.cardDetails, isCopy: true });
  };

  useEffect(() => {
    if (emailSuccess !== undefined) {
      setTimeout(() => {
        setEmailSuccess(undefined);
      }, 5000);
    }
  }, [emailSuccess]);

  const handleEmail = async (email?: string) => {
    if (!order) {
      return;
    }

    setEmailSuccess(undefined);

    try {
      const result = await sendOrderEmail({
        variables: { orderId: order.id, email: email || order.user.email },
      });

      if (result.data?.sendOrderEmail?.error && result.data?.sendOrderEmail?.message) {
        throw new Error(result.data.sendOrderEmail.message);
      }
      setEmailSuccess(true);
      reportsApi.receiptCopyEmailed(order.payment.amount);

      dispatch(addToast(`${i18n('TOAST.Email.Sent')} ${email || order.user.email}`, true));
      changeHasPrinted({
        variables: { id: order.id, hasPrintedReceiptCopy: true },
      });
    } catch (e) {
      console.log(e);
      dispatch(addToast(i18n('TOAST.Email.Not.Sent'), true));
      setEmailSuccess(false);
    }
  };

  function handleEmailOrderClicked() {
    toggleEmailModal();
  }

  return (
    <Wrapper>
      <IconWrapper
        onClick={() => handlePrint(ReceiptType.Copy)}
        disabled={order.hasPrintedReceiptCopy}>
        <PrintIcon className={`fa fa-print`} /> {i18n('History.PrintReceiptCopy')}
      </IconWrapper>

      {order.status === ORDER_STATUS.REFUNDED && (
        <IconWrapper onClick={() => handlePrint(ReceiptType.Refund)} disabled={false}>
          <PrintIcon className={`fa fa-print`} /> {i18n('History.PrintRefundReceiptCopy')}
        </IconWrapper>
      )}

      <IconWrapper onClick={handleEmailOrderClicked} disabled={false}>
        {loading ? (
          i18n('General.Sending')
        ) : (
          <>
            <PrintIcon className={`fa fa-envelope`} /> {i18n('History.EmailReceiptCopy')}
          </>
        )}
      </IconWrapper>
      <EmailMessage error={emailSuccess === false}>
        {emailSuccess === false && i18n('TOAST.Email.Not.Sent')}
        {emailSuccess === true && i18n('TOAST.Email.Success.Sent')}
      </EmailMessage>
      <Modal isShowing={emailModalShowing} hide={toggleEmailModal}>
        <MessageInput
          autoFocus={false}
          value={order.user.email || ''}
          onSubmit={(val) => {
            handleEmail(val.trim());
            toggleEmailModal();
          }}
          width="500px"
        />
      </Modal>
    </Wrapper>
  );
};

export default PrintButton;
